/**
 * DesiredDateTimeInput: 空き枠なし時のアポイントリクエスト（US-D2-04）における
 * 希望日時（第1候補必須、第2・第3候補任意）の入力UI。
 *
 * 2026-07-15 実装レビュー対応: multiCvAppointmentListUi.ts / preBookingFormUi.ts双方で
 * submitAppointmentRequestが要求するdesiredDateTime1〜3の入力手段が存在しなかった欠落
 * （Code Generation当初からの既存バグ、2026-07-15レビューで新規発見）への対応。
 * design-mockups/pre-booking-form.html Case3のカレンダーUIをフルスクラッチ実装する代わりに、
 * ネイティブ`<input type="datetime-local">`によるシンプルな実装とする（BR-U6-9: バニラJS方針）。
 *
 * 2026-09-02 バグ修正: カレンダーがアポ受付設定（対応可能ルール: 営業時間・除外日・最小リードタイム）
 * を一切見ずに「今日からAPPOINTMENT_RANGE_DAYS日以内なら選択可能」という決め打ちで選択可能日を
 * 決めていたため、当日等が選択可能に見えてしまい、選択して送信するとサーバ側の対応可能ルール検証
 * （SlotNotAvailableError）で拒否される不具合を修正。新規API（getAvailabilityRule）の結果を反映する。
 */

import { createElement } from "../core/shadowDomUtil";
import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning } from "../core/errorHandler";
import { APPOINTMENT_RANGE_DAYS } from "../core/appointmentConstants";
import { formatCandidateLabel as formatCandidateLabelUtil, buildRequestDateTimeFromKey, parseJstDateTime, nowJstDateTime, weekdayIndex } from "./datetime";
import type { AvailabilityRuleResult } from "../types";

export interface DesiredDateTimeValues {
  desiredDateTime1: string;
  desiredDateTime2?: string;
  desiredDateTime3?: string;
}

/**
 * 希望日時入力フォーム（第1〜第3候補）をcontainerへ追加する。
 * 各inputの値はJSTローカル文字列（`yyyy-MM-ddTHH:mm:ss`、オフセットなし）に整形して返す`getValues`を提供する。
 * 第1候補が未入力の場合、`getValues`はnullを返す（呼び出し元は送信ボタンの有効化制御等に利用）。
 */
export function renderDesiredDateTimeInputs(container: HTMLElement): {
  getValues: () => DesiredDateTimeValues | null;
} {
  const wrapper = createElement("div", { className: "spica-desired-datetime-inputs" });

  const label1 = createElement("label", { className: "spica-desired-datetime-label", text: "第1候補（必須）" });
  const input1 = createElement("input", { attrs: { type: "datetime-local" } });
  wrapper.appendChild(label1);
  wrapper.appendChild(input1);

  const label2 = createElement("label", { className: "spica-desired-datetime-label", text: "第2候補（任意）" });
  const input2 = createElement("input", { attrs: { type: "datetime-local" } });
  wrapper.appendChild(label2);
  wrapper.appendChild(input2);

  const label3 = createElement("label", { className: "spica-desired-datetime-label", text: "第3候補（任意）" });
  const input3 = createElement("input", { attrs: { type: "datetime-local" } });
  wrapper.appendChild(label3);
  wrapper.appendChild(input3);

  container.appendChild(wrapper);

  return {
    getValues(): DesiredDateTimeValues | null {
      if (!input1.value) return null;

      // `<input type="datetime-local">` の値は `yyyy-MM-ddTHH:mm`（ローカル壁時計、TZ非依存）。
      // これを `toISOString()`（UTC化）せず、秒を補って JST ローカル送信文字列 `yyyy-MM-ddTHH:mm:ss` にする（Q4=A）。
      const toRequestString = (value: string): string | undefined => {
        if (!value) return undefined;
        const p = parseJstDateTime(value);
        if (!p) return undefined;
        return `${value.length === 16 ? value : value.substring(0, 16)}:00`;
      };

      const desiredDateTime1 = toRequestString(input1.value);
      if (!desiredDateTime1) return null;

      return {
        desiredDateTime1,
        desiredDateTime2: toRequestString(input2.value),
        desiredDateTime3: toRequestString(input3.value),
      };
    },
  };
}

/**
 * 時刻候補生成の上限（表示用の安全弁）。営業開始からこの分数を超える候補は生成しない
 * （不正に広い営業時間設定でボタンが無限生成されるのを防ぐ）。24時間ぶん。
 */
const MAX_MINUTES_IN_DAY = 24 * 60;

/** `weekdayIndex`（0=日〜6=土）からspica-apiのDayOfWeek文字列（java.time.DayOfWeek準拠）への対応表。 */
const DAY_OF_WEEK_BY_INDEX = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"] as const;

/** 対応可能ルール取得の中継エンドポイント呼び出しがない/失敗した場合のフェイルセーフ値（全て選択不可）。 */
const FAILSAFE_AVAILABILITY_RULE: AvailabilityRuleResult = {
  minLeadTimeMinutes: Number.MAX_SAFE_INTEGER,
  slotDurationMinutes: 60,
  slotIncrementMinutes: 60,
  businessHours: [],
  excludedDays: [],
};

/**
 * 対応可能ルール（営業時間・除外日・最小リードタイム）を取得する。
 * 取得できない場合（memberGroupId未指定・APIエラー）はフェイルセーフ値を返し、カレンダーを
 * 「全て選択不可」にする（アポ受付設定を無視して選択可能に見せてしまう不具合の再発防止）。
 */
async function fetchAvailabilityRule(memberGroupId: string | null | undefined): Promise<AvailabilityRuleResult> {
  if (!memberGroupId) {
    return FAILSAFE_AVAILABILITY_RULE;
  }
  const response = await relayClient.getAvailabilityRule<AvailabilityRuleResult>({ memberGroupId });
  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    logClientWarning(`getAvailabilityRule failed: ${classified.kind}`, memberGroupId);
    return FAILSAFE_AVAILABILITY_RULE;
  }
  return response.data;
}

/**
 * 対応可能ルールから、指定日付キー（YYYY-MM-DD）が選択可能かを判定する。
 * 判定内容: 最小リードタイム（現在時刻+minLeadTimeMinutes以降）・除外日でない・
 * その曜日に営業時間帯が1つ以上設定されている（時間帯自体の判定は時間選択側で行う）。
 */
function isDateSelectable(dateKey: string, rule: AvailabilityRuleResult): boolean {
  const earliestBookable = calculateEarliestBookableDateKey(rule.minLeadTimeMinutes);
  if (dateKey < earliestBookable) return false;
  if (rule.excludedDays.includes(dateKey)) return false;

  const p = parseJstDateTime(dateKey);
  if (!p) return false;
  const dow = DAY_OF_WEEK_BY_INDEX[weekdayIndex(p.year, p.month, p.day)];
  return rule.businessHours.some((bh) => bh.dayOfWeek === dow);
}

/**
 * 現在時刻に最小リードタイム（分）を加えた「予約可能開始」の日付キー（YYYY-MM-DD）を返す。
 * 時刻部分は日付選択の判定にのみ使うため、日単位に落とす（同日内の時刻ちょうどの境界は
 * 時間選択側〔isTimeSelectable〕で正確に判定する）。
 */
function calculateEarliestBookableDateKey(minLeadTimeMinutes: number): string {
  const now = nowJstDateTime();
  const p = parseJstDateTime(now);
  if (!p) return "9999-12-31"; // パース不可時はフェイルセーフ（選択不可）
  const nowMs = new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second).getTime();
  const earliest = new Date(nowMs + minLeadTimeMinutes * 60 * 1000);
  return toDateKey(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
}

/** "HH:mm" または "HH:mm:ss" を、その日の0:00からの経過分に変換する。 */
function timeToMinutes(hhmm: string): number {
  const parts = hhmm.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  return h * 60 + m;
}

/** 0:00からの経過分を "HH:mm" に変換する。 */
function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * 指定日付の選択可能な時刻候補（"HH:mm"の配列）を、企業のアポ受付設定から動的生成する。
 *
 * <p>サーバ（{@code SlotCalculationService.isSlotBookableInternal}）の許可条件と一致させる:
 * その曜日の各営業時間帯レンジについて、営業開始時刻から{@code slotIncrementMinutes}刻みで候補を並べ、
 * 「開始 + {@code slotDurationMinutes}（＝終了時刻）が営業終了以内」の候補のみを採用する。
 * 複数レンジがある場合は全レンジの候補を昇順・重複排除で統合する。
 * 最小リードタイム判定は{@link isTimeSelectable}側で行う（ここでは営業時間・刻み・所要時間のみ反映）。
 */
function generateTimeSlotsForDate(dateKey: string, rule: AvailabilityRuleResult): string[] {
  const p = parseJstDateTime(dateKey);
  if (!p) return [];
  const dow = DAY_OF_WEEK_BY_INDEX[weekdayIndex(p.year, p.month, p.day)];
  const increment = rule.slotIncrementMinutes > 0 ? rule.slotIncrementMinutes : 60;
  const duration = rule.slotDurationMinutes > 0 ? rule.slotDurationMinutes : 60;

  const slots = new Set<number>();
  for (const bh of rule.businessHours) {
    if (bh.dayOfWeek !== dow) continue;
    const startMin = timeToMinutes(bh.startTime);
    const endMin = timeToMinutes(bh.endTime);
    // 営業開始から刻みで進め、終了(開始+所要)が営業終了以内の候補のみ採用（サーバのグリッド整列と一致）
    for (let t = startMin; t + duration <= endMin && t < MAX_MINUTES_IN_DAY; t += increment) {
      slots.add(t);
    }
  }
  return Array.from(slots)
    .sort((a, b) => a - b)
    .map(minutesToTime);
}

/**
 * 対応可能ルールから、指定日付キー・時刻（HH:mm）が選択可能かを判定する。
 * 判定内容: 最小リードタイム（分単位まで正確に判定）・その曜日の営業時間帯レンジ内かつ
 * 営業開始からの刻みグリッドに整列し所要時間が営業終了以内（サーバと一致）。
 */
function isTimeSelectable(dateKey: string, time: string, rule: AvailabilityRuleResult): boolean {
  const candidateIso = buildRequestDateTimeFromKey(dateKey, time);
  const earliestBookableIso = calculateEarliestBookableIso(rule.minLeadTimeMinutes);
  if (candidateIso < earliestBookableIso) return false;

  // 営業時間・刻み・所要時間から生成した候補に含まれるかで判定（サーバのグリッド整列と一致）
  return generateTimeSlotsForDate(dateKey, rule).includes(time);
}

/** 現在時刻+最小リードタイム（分）のJSTローカル送信文字列（`yyyy-MM-ddTHH:mm:ss`）を返す。 */
function calculateEarliestBookableIso(minLeadTimeMinutes: number): string {
  const now = nowJstDateTime();
  const p = parseJstDateTime(now);
  if (!p) return "9999-12-31T00:00:00";
  const nowMs = new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second).getTime();
  const earliest = new Date(nowMs + minLeadTimeMinutes * 60 * 1000);
  return `${earliest.getFullYear()}-${String(earliest.getMonth() + 1).padStart(2, "0")}-${String(earliest.getDate()).padStart(2, "0")}T${String(earliest.getHours()).padStart(2, "0")}:${String(earliest.getMinutes()).padStart(2, "0")}:${String(earliest.getSeconds()).padStart(2, "0")}`;
}

/**
 * カレンダーグリッド形式の希望日時入力（第1〜第3候補）。
 * アポイントのカレンダー（CalendarGridView）とデザイン形式を統一する:
 * アポ受付設定（営業時間・除外日・最小リードタイム）を反映して選択可能日・時間を決める
 * （2026-09-02バグ修正、対応可能ルールを見ずに選択可能になっていた不具合の対応）。
 * 各候補欄はクリックで編集対象（アクティブ候補）を切り替えられるため、第2候補選択後でも
 * 第1候補を選び直せる（2026-07-24レビュー反映）。
 */
export function renderDesiredDateTimeCalendarInputs(
  container: HTMLElement,
  memberGroupId?: string | null
): {
  getValues: () => DesiredDateTimeValues | null;
  /**
   * カレンダー本体（対応可能ルールAPI取得完了後に構築される）の準備完了を示すPromise。
   * 呼び出し元がカレンダーモーダルを開く際、この解決を待ってから開くことで、
   * `calendarModalUi.ts`の`reorderModalBody`（候補欄・カレンダー要素の並び替え）が
   * 正しく動作する（2026-09-02追加、非同期化に伴うモーダル並び替え崩れの修正）。
   */
  ready: Promise<void>;
} {
  const wrapper = createElement("div", { className: "spica-desired-datetime-inputs" });

  const candidates: { label: string; iso: string | null }[] = [
    { label: "第1候補", iso: null },
    { label: "第2候補（任意）", iso: null },
    { label: "第3候補（任意）", iso: null },
  ];
  let activeCandidateIndex = 0;

  // 2026-08-19デザイン更新: ヒント文「候補欄をタップすると、その候補の日時を選び直せます。」は
  // design-mockups側で冗長として削除されているため撤去した（候補欄クリックでの再選択機能自体は維持）。
  const candidateValues: HTMLElement[] = [];
  for (let i = 0; i < candidates.length; i++) {
    const label = createElement("p", { className: "spica-request-candidate-label", text: candidates[i].label });
    const value = createElement("div", { className: "spica-request-candidate-value empty", text: "未選択" });
    // 候補欄クリックで編集対象（アクティブ候補）を切り替える → 第1候補にも戻って選び直せる
    value.addEventListener("click", () => {
      activeCandidateIndex = i;
      refreshCandidateDisplay();
      // アクティブ候補が変わると「他候補で選択済み」の判定基準も変わるため、
      // 日付選択済みなら時刻ボタンを再描画して無効化状態を更新する（2026-09-10バグ修正）。
      if (currentDateKey) {
        activateTimeButtons(currentDateKey);
      }
    });
    wrapper.appendChild(label);
    wrapper.appendChild(value);
    candidateValues.push(value);
  }

  const calendarContainer = createElement("div");
  wrapper.appendChild(calendarContainer);

  function refreshCandidateDisplay(): void {
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      if (c.iso) {
        candidateValues[i].textContent = formatCandidateLabel(c.iso);
        candidateValues[i].classList.remove("empty");
      } else {
        candidateValues[i].textContent = "未選択";
        candidateValues[i].classList.add("empty");
      }
      candidateValues[i].classList.toggle("active", i === activeCandidateIndex);
    }
  }

  function findNextCandidateIndex(): number {
    const idx = candidates.findIndex((c) => c.iso === null);
    return idx === -1 ? activeCandidateIndex : idx;
  }

  // 対応可能ルール取得中はローディング表示（BusinessHourRange取得はネットワーク依存のため非同期）。
  const loadingLabel = createElement("p", { className: "spica-request-note", text: "対応可能な日時を確認しています..." });
  calendarContainer.appendChild(loadingLabel);

  const daySlots = createElement("div", { className: "spica-calendar-day-slots" });
  const timeList = createElement("ul", { className: "spica-calendar-slot-list" });
  daySlots.appendChild(timeList);
  // 日付未選択時の案内（時刻候補は日付選択後に営業時間帯から動的生成する）
  const noDateHint = createElement("li", { className: "spica-calendar-slot-hint", text: "日付を選択してください" });
  timeList.appendChild(noDateHint);

  let currentDateKey: string | null = null;
  let availabilityRule: AvailabilityRuleResult = FAILSAFE_AVAILABILITY_RULE;

  /**
   * 選択された日付の営業時間帯・刻み・所要時間・最小リードタイムから時刻候補を動的生成し、
   * 時刻ボタンを再構築する（固定リストを廃止、サーバのグリッド整列と一致させる）。
   */
  function activateTimeButtons(dateKey: string): void {
    currentDateKey = dateKey;
    timeList.textContent = "";
    const times = generateTimeSlotsForDate(dateKey, availabilityRule);
    const selectableTimes = times.filter((time) => isTimeSelectable(dateKey, time, availabilityRule));
    if (selectableTimes.length === 0) {
      timeList.appendChild(createElement("li", { className: "spica-calendar-slot-hint", text: "選択可能な時間がありません" }));
      return;
    }
    for (const time of selectableTimes) {
      const iso = buildIsoFromDateAndTime(dateKey, time);
      // 2026-09-10 バグ修正: 第1〜第3候補で同一日時を重複選択できてしまう問題への対応。
      // 現在編集中のアクティブ候補以外で既に同じ日時が選択されている場合は、その時刻ボタンを
      // 無効化する（送信前＝入力段階で重複を防ぐ。サーバ側の重複バリデーションと二重で担保）。
      const usedByOther = isDateTimeUsedByOtherCandidate(iso, activeCandidateIndex);
      const isSelectedByActive = candidates[activeCandidateIndex].iso === iso;
      const btn = createElement("button", {
        className: usedByOther
          ? "spica-calendar-slot-btn disabled"
          : isSelectedByActive
            ? "spica-calendar-slot-btn selected"
            : "spica-calendar-slot-btn",
        text: time,
      }) as HTMLButtonElement;
      if (usedByOther) {
        btn.disabled = true;
        btn.title = "他の候補で選択済みの日時です";
      } else {
        btn.addEventListener("click", () => {
          if (!currentDateKey) return;
          const selectedIso = buildIsoFromDateAndTime(currentDateKey, time);
          candidates[activeCandidateIndex].iso = selectedIso;
          activeCandidateIndex = findNextCandidateIndex();
          refreshCandidateDisplay();
          // 2026-09-10 追加修正: 候補確定直後にも時刻ボタンを再描画する。
          // これをしないと、いま選択した日時が「次のアクティブ候補」視点で無効化されず、
          // 同じ時刻ボタンを連続クリックして第2・第3候補に同一日時を入れられてしまう
          // （「最初の選択時に同じ日時を選択できてしまう」不具合の直接原因）。
          // 再描画により、確定済み日時のボタンはdisabled化され、選択済みボタンには
          // selectedクラスが付与される（activateTimeButtons内で判定）。
          activateTimeButtons(currentDateKey);
        });
      }
      const li = createElement("li");
      li.appendChild(btn);
      timeList.appendChild(li);
    }
  }

  /**
   * 指定した日時（iso）が、`exceptIndex`以外の候補で既に選択済みかを判定する。
   * 第1〜第3候補で同一日時を重複して選択させないためのガード（2026-09-10バグ修正）。
   */
  function isDateTimeUsedByOtherCandidate(iso: string, exceptIndex: number): boolean {
    return candidates.some((c, i) => i !== exceptIndex && c.iso === iso);
  }

  const ready = fetchAvailabilityRule(memberGroupId).then((rule) => {
    availabilityRule = rule;
    loadingLabel.remove();
    calendarContainer.appendChild(daySlots);
    renderRequestCalendar(calendarContainer, availabilityRule, (dateKey) => activateTimeButtons(dateKey));
  });

  refreshCandidateDisplay();

  container.appendChild(wrapper);

  return {
    getValues(): DesiredDateTimeValues | null {
      if (!candidates[0].iso) return null;
      return {
        desiredDateTime1: candidates[0].iso,
        desiredDateTime2: candidates[1].iso ?? undefined,
        desiredDateTime3: candidates[2].iso ?? undefined,
      };
    },
    ready,
  };
}

/** 希望日程候補の表示ラベル（例: "7月16日（木） 14:00"）を組み立てる。 */
function formatCandidateLabel(iso: string): string {
  // JSTローカル文字列を文字列操作で整形する（`new Date(iso)` 不使用、TZ非依存。datetime-contract.md §4）。
  return formatCandidateLabelUtil(iso);
}

/** 日付キー（YYYY-MM-DD）と時刻（"HH:MM"）からJSTローカル送信文字列（オフセットなし）を組み立てる。 */
function buildIsoFromDateAndTime(dateKey: string, time: string): string {
  // `toISOString()`（UTC化）せず、JSTローカル文字列 `yyyy-MM-ddTHH:mm:ss` を組み立てる（Q4=A）。
  return buildRequestDateTimeFromKey(dateKey, time);
}

/** 年・月(0-indexed)・日から日付キー（YYYY-MM-DD）を組み立てる。 */
function toDateKey(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/**
 * アポイントリクエスト用カレンダー。アポイントのカレンダー（CalendarGridView）とデザイン形式を統一する。
 * アポ受付設定（対応可能ルール: 営業時間・除外日・最小リードタイム）を反映して選択可能日を決める
 * （2026-09-02バグ修正）。上限（APPOINTMENT_RANGE_DAYS）は選択範囲の上限として維持する。
 */
function renderRequestCalendar(
  container: HTMLElement,
  rule: AvailabilityRuleResult,
  onDateSelected: (dateKey: string) => void
): void {
  const now = new Date();
  const maxDate = new Date(now.getTime() + APPOINTMENT_RANGE_DAYS * 24 * 60 * 60 * 1000);
  const maxDateKey = toDateKey(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  let viewYear = now.getFullYear();
  let viewMonth = now.getMonth();
  let selectedDateKey: string | null = null;

  const header = createElement("div", { className: "spica-calendar-header" });
  const prevButton = createElement("button", { className: "spica-calendar-nav-btn", text: "<" });
  const monthLabel = createElement("span", { className: "spica-calendar-month-label" });
  const nextButton = createElement("button", { className: "spica-calendar-nav-btn", text: ">" });
  header.appendChild(prevButton);
  header.appendChild(monthLabel);
  header.appendChild(nextButton);
  container.appendChild(header);

  const grid = createElement("div", { className: "spica-calendar-grid" });
  container.appendChild(grid);

  const weekdayLabels = ["日", "月", "火", "水", "木", "金", "土"];

  function renderMonth(): void {
    monthLabel.textContent = `${viewYear}年${viewMonth + 1}月`;
    grid.textContent = "";
    for (const w of weekdayLabels) {
      grid.appendChild(createElement("div", { className: "spica-calendar-weekday", text: w }));
    }

    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    for (let i = 0; i < firstDay.getDay(); i++) {
      grid.appendChild(createElement("span"));
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = toDateKey(viewYear, viewMonth, day);
      // アポ受付設定（営業時間・除外日・最小リードタイム）を反映、かつAPPOINTMENT_RANGE_DAYS以内
      const isSelectable = dateKey <= maxDateKey && isDateSelectable(dateKey, rule);
      const dayButton = createElement("button", {
        className: isSelectable ? "spica-calendar-day has-slot" : "spica-calendar-day disabled",
        text: String(day),
      }) as HTMLButtonElement;
      dayButton.disabled = !isSelectable;
      if (dateKey === selectedDateKey) dayButton.classList.add("selected");
      if (isSelectable) {
        dayButton.addEventListener("click", () => {
          selectedDateKey = dateKey;
          renderMonth();
          onDateSelected(dateKey);
        });
      }
      grid.appendChild(dayButton);
    }
  }

  prevButton.addEventListener("click", () => {
    viewMonth -= 1;
    if (viewMonth < 0) {
      viewMonth = 11;
      viewYear -= 1;
    }
    renderMonth();
  });
  nextButton.addEventListener("click", () => {
    viewMonth += 1;
    if (viewMonth > 11) {
      viewMonth = 0;
      viewYear += 1;
    }
    renderMonth();
  });

  renderMonth();
}
